import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import { ExtractedArticleInformationType } from "../types/ExtractedArticleInformationType";
import { SymbologyType } from "../types/SymbologyType";
import {
  extractProductInformationFromDataMatrix,
  extractProductInformationFromITF,
  extractProductInformationFromEAN13,
  extractProductInformationFromUPC,
  extractProductInformationFromArticleNumber,
  extractProductInformationFromQrCode,
  extractProductInformationFromFoodArticleNumber,
} from "./productInformationExtractorHelper";

type ExtractorFunction = (input: string) => ExtractedArticleInformationType;

const extractorMap: Record<string, ExtractorFunction> = {
  DATAMATRIX: extractProductInformationFromDataMatrix,
  DATA_MATRIX: extractProductInformationFromDataMatrix,
  ITF: extractProductInformationFromITF,
  EAN13: extractProductInformationFromEAN13,
  EAN_13: extractProductInformationFromEAN13,
  QR_CODE: extractProductInformationFromQrCode,
  UPC: extractProductInformationFromUPC,
  ARTICLE_NUMBER: extractProductInformationFromArticleNumber,
  INCOMPLETE: extractProductInformationFromArticleNumber,
  FOOD_ARTICLE: extractProductInformationFromFoodArticleNumber,
};

export function extractBasedOnSymbology(
  symbology: SymbologyType,
  input: string
): ExtractedArticleInformationType {
  if (input.length === 0) {
    return { articleNumber: "", supplierNumber: "", dateStamp: "" };
  }

  const formattedSymbology = symbology.toUpperCase();
  const extractor = extractorMap[formattedSymbology];

  if (!extractor) {
    throw new Error(
      `The symbology "${formattedSymbology}" is not supported. Input: ${input}`,
      {
        cause: { code: "INVALID_SYMBOLOGY", input },
      }
    );
  }

  try {
    return extractor(input);
  } catch (error) {
    reportMessageToSentry(
      `Error extracting product information for symbology "${formattedSymbology}" with input: ${input}`,
      {
        symbology: formattedSymbology,
        input,
        errorMessage: JSON.stringify(error),
      },
      "error"
    );
    throw new Error(
      `Error extracting product information for symbology "${formattedSymbology}" with input: ${input}`,
      {
        cause: { code: "EXTRACTION_ERROR", input, originalError: error },
      }
    );
  }
}
